import React, { useEffect, useState } from 'react';
import { useCachedAppImageSrc } from './common/CachedAppImage';
import {
  getCachedUserProfileImageUrl,
  peekCachedUserProfileImageUrl,
  type UserProfileImageCacheRef,
} from '../utils/userProfileImageCache';

export type UserProfileAvatarProps = {
  userId: string;
  custom: UserProfileImageCacheRef | null;
  googleAvatarUrl: string | null;
  className?: string;
  alt?: string;
};

const UserProfileAvatar: React.FC<UserProfileAvatarProps> = ({
  userId,
  custom,
  googleAvatarUrl,
  className,
  alt = '',
}) => {
  const defaultSrc = useCachedAppImageSrc('character_2.webp');
  const [customSrc, setCustomSrc] = useState<string | null>(() =>
    custom ? peekCachedUserProfileImageUrl(custom) ?? (custom.url.trim() || null) : null
  );
  const [customFailed, setCustomFailed] = useState(false);
  const [googleFailed, setGoogleFailed] = useState(false);
  const [defaultFailed, setDefaultFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCustomFailed(false);
    setGoogleFailed(false);
    setDefaultFailed(false);
    if (!custom) {
      setCustomSrc(null);
      return undefined;
    }
    const peeked = peekCachedUserProfileImageUrl(custom);
    setCustomSrc(peeked ?? (custom.url.trim() || null));
    void getCachedUserProfileImageUrl(custom).then((url) => {
      if (cancelled) return;
      if (url) setCustomSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, custom?.path, custom?.url, custom?.version]);

  const src =
    custom && !customFailed && customSrc
      ? customSrc
      : googleAvatarUrl && !googleFailed
        ? googleAvatarUrl
        : !defaultFailed
          ? defaultSrc
          : null;

  if (!src) return null;

  return (
    <img
      className={className}
      src={src}
      alt={alt}
      draggable={false}
      referrerPolicy="no-referrer"
      onError={() => {
        if (custom && !customFailed && src === customSrc) {
          setCustomFailed(true);
          return;
        }
        if (googleAvatarUrl && !googleFailed && src === googleAvatarUrl) {
          setGoogleFailed(true);
          return;
        }
        setDefaultFailed(true);
      }}
    />
  );
};

export default UserProfileAvatar;
